import { useUserStore } from '@/stores/user'
import { messages } from '@/i18n'

const LOCALE_STORAGE_KEY = 'app_locale'

function getMessage(locale: string, key: string): string | undefined {
  return messages[locale as keyof typeof messages]?.[key as keyof typeof messages.en]
}

/**
 * 替换模板中的占位符 {key}
 */
function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? `{${key}}`))
}

/**
 * 从英中混排文本中提取指定语言部分。
 * 例如 "Trace Code 溯源码"：en -> "Trace Code", zh-CN -> "溯源码"
 */
function extractByLocale(text: string, locale: string): string {
  // CJK 范围：基本汉字、扩展 A、假名、谚文等
  const cjkRegex = /[一-鿿㐀-䶿぀-ゟ゠-ヿ가-힯]/
  const match = text.match(cjkRegex)
  if (!match) return text

  const index = match.index!
  if (locale === 'zh-CN') {
    return text.slice(index).trim()
  }
  return text.slice(0, index).trim()
}

function resolveLocale(userStore: ReturnType<typeof useUserStore>): string {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
  if (stored === 'zh-CN' || stored === 'en') {
    return stored
  }
  if (userStore.userInfo?.language === 'zh-CN' || userStore.userInfo?.language === 'en') {
    return userStore.userInfo.language
  }
  return userStore.isSuperAdmin ? 'zh-CN' : 'en'
}

/**
 * Internationalization composable
 * - Supports legacy t(en, zh) calls
 * - Supports key-based t(key) calls with English fallback
 * - Supports interpolation t(key, { n: 5 })
 * - Reads locale from localStorage > user profile > role default
 */
export function useI18n() {
  const userStore = useUserStore()
  const locale = resolveLocale(userStore)

  /**
   * Translate text.
   * - t('common.save') -> lookup by key
   * - t('common.years', { n: 5 }) -> lookup with interpolation
   * - t('English', '中文') -> legacy combined label
   */
  function t(first: string, second?: string | Record<string, string | number>): string {
    if (typeof second === 'string') {
      // legacy signature: t('English', '中文')
      return locale === 'zh-CN' ? second : first
    }

    // key-based
    const msg = getMessage(locale, first) || getMessage('en', first)
    if (msg) {
      return second ? interpolate(msg, second) : msg
    }

    // 尝试从英中混排文本中提取（兼容旧代码）
    return extractByLocale(first, locale)
  }

  function setLocale(newLocale: 'en' | 'zh-CN') {
    localStorage.setItem(LOCALE_STORAGE_KEY, newLocale)
    // Caller is responsible for reloading or re-rendering; API update is done by the account page.
  }

  return {
    t,
    locale,
    setLocale
  }
}
